import React, { useState, useEffect, useRef, useMemo } from 'react';
import { urls } from './config.dev';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faUserPlus, faUser, faCheckCircle, faArrowLeft,
  faTimesCircle, faClock, faHospital, faMoneyBillWave,
  faShieldAlt, faHistory, faCalendarAlt, faChartLine,
  faArrowRight, faExclamationTriangle, faPhone,
  faEnvelope, faMapMarkerAlt, faInfoCircle, faUsers, faWallet, faTimes, faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';

// ─── Theme System ──────────────────────────────────────────────────────────────
const THEMES = {
  white: {
    name: 'white',
    sidebar: '#ffffff', sidebarBorder: '#e8ecf0', sidebarText: '#1a2332',
    sidebarMuted: '#64748b', sidebarAccent: '#2563eb',
    sidebarCard: '#f8fafc', sidebarCardBorder: '#e2e8f0',
    topbar: '#ffffff', topbarBorder: '#e8ecf0',
    mainBg: '#f1f5f9', cardBg: '#ffffff', cardBorder: '#e2e8f0',
    primary: '#2563eb', primaryGrad: 'linear-gradient(135deg,#2563eb,#4f46e5)',
    success: '#059669', successGrad: 'linear-gradient(135deg,#059669,#10b981)',
    text: '#0f172a', textMuted: '#64748b', textLight: '#94a3b8',
    balanceBg: 'linear-gradient(135deg,#eff6ff,#e0e7ff)',
    balanceBorder: '#bfdbfe', balanceText: '#1e40af',
    logoGrad: 'linear-gradient(135deg,#2563eb,#4f46e5)',
  },
  blue: {
    name: 'blue',
    sidebar: '#0a1628', sidebarBorder: 'rgba(255,255,255,0.07)', sidebarText: '#e2e8f0',
    sidebarMuted: '#64748b', sidebarAccent: '#60a5fa',
    sidebarCard: 'rgba(255,255,255,0.05)', sidebarCardBorder: 'rgba(255,255,255,0.08)',
    topbar: '#0d1f3c', topbarBorder: 'rgba(255,255,255,0.06)',
    mainBg: '#f1f5f9', cardBg: '#ffffff', cardBorder: '#e2e8f0',
    primary: '#3b82f6', primaryGrad: 'linear-gradient(135deg,#3b82f6,#818cf8)',
    success: '#059669', successGrad: 'linear-gradient(135deg,#10b981,#059669)',
    text: '#0f172a', textMuted: '#64748b', textLight: '#94a3b8',
    balanceBg: 'linear-gradient(135deg,rgba(59,130,246,0.15),rgba(129,140,248,0.15))',
    balanceBorder: 'rgba(59,130,246,0.25)', balanceText: '#fff',
    logoGrad: 'linear-gradient(135deg,#3b82f6,#818cf8)',
  },
  emerald: {
    name: 'emerald',
    sidebar: '#064e3b', sidebarBorder: 'rgba(255,255,255,0.07)', sidebarText: '#d1fae5',
    sidebarMuted: '#6ee7b7', sidebarAccent: '#34d399',
    sidebarCard: 'rgba(255,255,255,0.06)', sidebarCardBorder: 'rgba(255,255,255,0.1)',
    topbar: '#065f46', topbarBorder: 'rgba(255,255,255,0.06)',
    mainBg: '#f0fdf4', cardBg: '#ffffff', cardBorder: '#bbf7d0',
    primary: '#059669', primaryGrad: 'linear-gradient(135deg,#059669,#10b981)',
    success: '#059669', successGrad: 'linear-gradient(135deg,#059669,#10b981)',
    text: '#0f172a', textMuted: '#64748b', textLight: '#94a3b8',
    balanceBg: 'linear-gradient(135deg,rgba(52,211,153,0.2),rgba(16,185,129,0.15))',
    balanceBorder: 'rgba(52,211,153,0.3)', balanceText: '#d1fae5',
    logoGrad: 'linear-gradient(135deg,#059669,#34d399)',
  },
  violet: {
    name: 'violet',
    sidebar: '#1e1b4b', sidebarBorder: 'rgba(255,255,255,0.07)', sidebarText: '#e0e7ff',
    sidebarMuted: '#a5b4fc', sidebarAccent: '#818cf8',
    sidebarCard: 'rgba(255,255,255,0.05)', sidebarCardBorder: 'rgba(255,255,255,0.08)',
    topbar: '#2d2a6e', topbarBorder: 'rgba(255,255,255,0.06)',
    mainBg: '#f5f3ff', cardBg: '#ffffff', cardBorder: '#ddd6fe',
    primary: '#7c3aed', primaryGrad: 'linear-gradient(135deg,#7c3aed,#a78bfa)',
    success: '#059669', successGrad: 'linear-gradient(135deg,#10b981,#059669)',
    text: '#0f172a', textMuted: '#64748b', textLight: '#94a3b8',
    balanceBg: 'linear-gradient(135deg,rgba(124,58,237,0.2),rgba(167,139,250,0.15))',
    balanceBorder: 'rgba(124,58,237,0.3)', balanceText: '#e0e7ff',
    logoGrad: 'linear-gradient(135deg,#7c3aed,#a78bfa)',
  },
  rose: {
    name: 'rose',
    sidebar: '#881337', sidebarBorder: 'rgba(255,255,255,0.07)', sidebarText: '#ffe4e6',
    sidebarMuted: '#fda4af', sidebarAccent: '#fb7185',
    sidebarCard: 'rgba(255,255,255,0.06)', sidebarCardBorder: 'rgba(255,255,255,0.1)',
    topbar: '#9f1239', topbarBorder: 'rgba(255,255,255,0.07)',
    mainBg: '#fff1f2', cardBg: '#ffffff', cardBorder: '#fecdd3',
    primary: '#e11d48', primaryGrad: 'linear-gradient(135deg,#e11d48,#fb7185)',
    success: '#059669', successGrad: 'linear-gradient(135deg,#10b981,#059669)',
    text: '#0f172a', textMuted: '#64748b', textLight: '#94a3b8',
    balanceBg: 'linear-gradient(135deg,rgba(225,29,72,0.2),rgba(251,113,133,0.15))',
    balanceBorder: 'rgba(225,29,72,0.3)', balanceText: '#ffe4e6',
    logoGrad: 'linear-gradient(135deg,#e11d48,#fb7185)',
  },
};
const RANDOM_THEMES = ['emerald', 'violet', 'rose'];

// ─── Country Data ──────────────────────────────────────────────────────────────
const countryData = {
  'Uganda':       { iso:'UG', code:'256', currency:'UGX', flag:'🇺🇬' },
  'Kenya':        { iso:'KE', code:'254', currency:'KES', flag:'🇰🇪' },
  'Rwanda':       { iso:'RW', code:'250', currency:'RWF', flag:'🇷🇼' },
  'Tanzania':     { iso:'TZ', code:'255', currency:'TZS', flag:'🇹🇿' },
  'Nigeria':      { iso:'NG', code:'234', currency:'NGN', flag:'🇳🇬' },
  'Ghana':        { iso:'GH', code:'233', currency:'GHS', flag:'🇬🇭' },
  'South Africa': { iso:'ZA', code:'27',  currency:'ZAR', flag:'🇿🇦' },
  'Ethiopia':     { iso:'ET', code:'251', currency:'ETB', flag:'🇪🇹' },
  'DR Congo':     { iso:'CD', code:'243', currency:'CDF', flag:'🇨🇩' },
  'Sudan':        { iso:'SD', code:'249', currency:'SDG', flag:'🇸🇩' },
};

const NETWORK_PREFIXES = { MTN:['77','78','76'], AIRTEL:['70','75','74'] };

const validateNetworkPrefix = (phoneNumber, network) => {
  if (!phoneNumber || !network) return { isValid:true, warning:'' };
  const clean = phoneNumber.replace(/\D/g,'');
  if (clean.length < 11) return { isValid:true, warning:'' };
  const prefix = clean.substring(3,5);
  const net = network.toUpperCase();
  if (net==='MTN'    && NETWORK_PREFIXES.AIRTEL.includes(prefix))
    return { isValid:false, warning:`This number (+256${prefix}…) typically belongs to AIRTEL, not MTN.` };
  if (net==='AIRTEL' && NETWORK_PREFIXES.MTN.includes(prefix))
    return { isValid:false, warning:`This number (+256${prefix}…) typically belongs to MTN, not AIRTEL.` };
  return { isValid:true, warning:'' };
};

// ─── Payment Mode Config ───────────────────────────────────────────────────────
const PAYMENT_MODES = [
  { id:'wallet',  label:'Wallet',   icon:faWallet,      shortLabel:'Wallet Deduction' },
  { id:'3months', label:'3 Months', icon:faCalendarAlt, shortLabel:'3-Month Plan' },
  { id:'6months', label:'6 Months', icon:faCalendarAlt, shortLabel:'6-Month Plan' },
  { id:'9months', label:'9 Months', icon:faCalendarAlt, shortLabel:'9-Month Plan' },
  { id:'1year',   label:'1 Year',   icon:faChartLine,   shortLabel:'Annual Plan' },
];

/*
 * ════════════════════════════════════════════════════════════════
 *  PRICING RULES
 *  Days = months × 30  (1 year = 12 × 30 = 360 days)
 *
 *  Employee tier   Rate used
 *  ─────────────── ──────────────────────────────────────────────
 *  1 employee      days × 700                    (no discount)
 *  2 employees     days × 600                    (no discount)
 *  3–10 employees  days × plan_rateMulti × count × 0.9 (10% off)
 *  above 10        flat rate per plan
 *
 *  Plan      months  rateMulti  flatAbove10
 *  ────────  ──────  ─────────  ───────────
 *  3months      3      600        400,000
 *  6months      6      500        820,000
 *  9months      9      400      1,000,000
 *  1year       12      370      1,300,000
 * ════════════════════════════════════════════════════════════════
 */
const PLAN_CONFIG = {
  '3months': { months:3,  rateMulti:600, flatAbove10:400000   },
  '6months': { months:6,  rateMulti:500, flatAbove10:820000   },
  '9months': { months:9,  rateMulti:400, flatAbove10:1000000  },
  '1year':   { months:12, rateMulti:370, flatAbove10:1300000  },
};

const calculateAmount = (modeId, employeeCount) => {
  if (modeId === 'wallet') return null;
  const cfg = PLAN_CONFIG[modeId];
  if (!cfg) return null;
  const days = cfg.months * 30;
  if (employeeCount === 'above10') return cfg.flatAbove10;
  const count = parseInt(employeeCount, 10);
  if (count === 1) return days * 700;                           // fixed, no discount
  if (count === 2) return days * 600;                           // fixed, no discount
  return Math.round(days * cfg.rateMulti * count * 0.9);        // 10% discount for 3-10
};

// ─── Mode description rows (built dynamically) ────────────────────────────────
const buildRows = (modeId) => {
  const cfg  = PLAN_CONFIG[modeId];
  const days = cfg.months * 30;
  const f    = (n) => n.toLocaleString();
  return [
    { label:'1 employee',      value:`${days} × 700 = ${f(days*700)} UGX`,                               highlight:false },
    { label:'2 employees',     value:`${days} × 600 = ${f(days*600)} UGX`,                               highlight:false },
    { label:'3–10 employees',  value:`${days} × ${cfg.rateMulti} × count − 10%`,                         highlight:false },
    { label:'Above 10 (flat)', value:`${f(cfg.flatAbove10)} UGX`,                                        highlight:true  },
  ];
};

const MODE_META = {
  wallet: {
    title:'Wallet Deduction', emoji:'💳',
    color:'#10b981', bgColor:'rgba(16,185,129,0.10)', borderColor:'rgba(16,185,129,0.35)',
    gradStart:'#10b981', gradEnd:'#059669',
    tagline:'Pay as you go — only for active employees',
    description:'Load money as wallet credit. The system automatically deducts daily based on employees who were active in the past 24 hours.',
    rows:[
      { label:'1 employee active',   value:'700 UGX / day',             highlight:false },
      { label:'2+ employees active', value:'600 UGX / employee / day',  highlight:false },
      { label:'Employee limit',      value:'Unlimited',                  highlight:true  },
      { label:'Billing cycle',       value:'Daily — only active staff',  highlight:false },
      { label:'Top-up',              value:'Anytime, any amount',        highlight:false },
    ],
    note:'Best for clinics with variable staffing. No long-term commitment required.',
  },
  '3months': {
    title:'3-Month Plan', emoji:'📅',
    color:'#3b82f6', bgColor:'rgba(59,130,246,0.10)', borderColor:'rgba(59,130,246,0.35)',
    gradStart:'#3b82f6', gradEnd:'#2563eb',
    tagline:'90 days of uninterrupted access',
    description:'Prepaid 90-day subscription (3 × 30 days). Pay once and enjoy full access — no daily deductions.',
    get rows() { return buildRows('3months'); },
    note:'Great for stable clinics that want predictable, worry-free billing.',
  },
  '6months': {
    title:'6-Month Plan', emoji:'🗓️',
    color:'#8b5cf6', bgColor:'rgba(139,92,246,0.10)', borderColor:'rgba(139,92,246,0.35)',
    gradStart:'#8b5cf6', gradEnd:'#7c3aed',
    tagline:'180 days — better multi-staff rate',
    description:'Prepaid 180-day subscription (6 × 30 days). Teams of 3–10 enjoy a lower rate of 500 UGX/day.',
    get rows() { return buildRows('6months'); },
    note:'Better value than the 3-month plan for growing clinics.',
  },
  '9months': {
    title:'9-Month Plan', emoji:'⏳',
    color:'#f59e0b', bgColor:'rgba(245,158,11,0.10)', borderColor:'rgba(245,158,11,0.35)',
    gradStart:'#f59e0b', gradEnd:'#d97706',
    tagline:'270 days — plan well ahead',
    description:'Prepaid 270-day subscription (9 × 30 days). Teams of 3–10 get 400 UGX/day — strong mid-term savings.',
    get rows() { return buildRows('9months'); },
    note:'Ideal for clinics planning well ahead with stable staffing.',
  },
  '1year': {
    title:'Annual Plan', emoji:'🏆',
    color:'#ef4444', bgColor:'rgba(239,68,68,0.10)', borderColor:'rgba(239,68,68,0.35)',
    gradStart:'#ef4444', gradEnd:'#dc2626',
    tagline:'360 days — full year of coverage',
    description:'Prepaid 360-day subscription (12 × 30 days). The ultimate long-term plan — teams of 3–10 get 370 UGX/day.',
    get rows() { return buildRows('1year'); },
    note:'Maximum savings for established multi-staff clinics.',
  },
};

// ─── Custom Toast/Prompt Component ────────────────────────────────────────────
const CustomPrompt = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onClose) onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : type === 'warning' ? '#f59e0b' : '#3b82f6';
  const icon = type === 'success' ? faCheckCircle : type === 'error' ? faTimesCircle : type === 'warning' ? faExclamationTriangle : faInfoCircle;

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      backgroundColor: bgColor,
      color: 'white',
      padding: '14px 22px',
      borderRadius: '12px',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
      animation: 'slideIn 0.3s ease-out',
      fontFamily: "'DM Sans', system-ui, sans-serif",
      fontSize: '13px',
      fontWeight: '500',
      maxWidth: '380px'
    }}>
      <FontAwesomeIcon icon={icon} style={{ fontSize: '18px' }} />
      <span>{message}</span>
    </div>
  );
};

// ─── Confirmation Dialog Component ────────────────────────────────────────────
const ConfirmationDialog = ({ isOpen, onConfirm, onCancel, title, message, confirmText, cancelText, danger, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      animation: 'fadeIn 0.2s ease-out'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        maxWidth: '480px',
        width: '90%',
        padding: '28px',
        boxShadow: '0 25px 40px -12px rgba(0,0,0,0.25)',
        animation: 'scaleIn 0.2s ease-out'
      }}>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: danger ? '#fee2e2' : '#eff6ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px'
        }}>
          <FontAwesomeIcon icon={danger ? faExclamationTriangle : faInfoCircle} style={{ fontSize: '28px', color: danger ? '#ef4444' : '#3b82f6' }} />
        </div>
        <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', marginBottom: '12px', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
          {title}
        </h3>
        <p style={{ fontSize: '14px', color: '#475569', marginBottom: '28px', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: '14px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f1f5f9',
              border: 'none',
              borderRadius: '10px',
              color: '#64748b',
              fontWeight: '700',
              fontSize: '13px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: isLoading ? 0.6 : 1
            }}
            onMouseEnter={e => { if (!isLoading) e.currentTarget.style.backgroundColor = '#e2e8f0'; }}
            onMouseLeave={e => { if (!isLoading) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
          >
            {cancelText || 'Cancel'}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            style={{
              padding: '10px 24px',
              backgroundColor: danger ? '#ef4444' : '#3b82f6',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontWeight: '700',
              fontSize: '13px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: isLoading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={e => { if (!isLoading) e.currentTarget.style.backgroundColor = danger ? '#dc2626' : '#2563eb'; }}
            onMouseLeave={e => { if (!isLoading) e.currentTarget.style.backgroundColor = danger ? '#ef4444' : '#3b82f6'; }}
          >
            {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
            {confirmText || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Loading Overlay Component ────────────────────────────────────────────────
const LoadingOverlay = ({ message }) => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001,
    backdropFilter: 'blur(3px)'
  }}>
    <div style={{
      background: 'white',
      borderRadius: '20px',
      padding: '28px 36px',
      textAlign: 'center',
      boxShadow: '0 20px 35px -10px rgba(0,0,0,0.2)',
      animation: 'scaleIn 0.2s ease-out'
    }}>
      <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '40px', color: '#3b82f6', marginBottom: '16px' }} />
      <p style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: 0 }}>{message || 'Processing...'}</p>
    </div>
  </div>
);

// ─── Component ─────────────────────────────────────────────────────────────────
function PaymentNew() {

  const resolvedTheme = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const t = (params.get('theme') || '').toLowerCase();
    if (THEMES[t]) return THEMES[t];
    return THEMES[RANDOM_THEMES[Math.floor(Math.random() * RANDOM_THEMES.length)]];
  }, []);
  const T = resolvedTheme;
  const isDarkSidebar = T.name !== 'white';

  const [view, setView] = useState('selectPayer');
  const [showNewPayerForm, setShowNewPayerForm] = useState(false);
  const [clinicDetails, setClinicDetails] = useState({
    healthFacilityName:'', country:'', countryIso:'UG', district:'', town:'', subCounty:'',
    currentBalance:0, currency:'UGX', token:'', modeAmount:'0',
  });
  const [payers, setPayers] = useState([]);
  const [selectedPayer, setSelectedPayer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingPayers, setFetchingPayers] = useState(false);
  const [errors, setErrors] = useState({});
  const [networkWarning, setNetworkWarning] = useState('');
  const [forceNetworkConfirmation, setForceNetworkConfirmation] = useState(false);
  const [activeMode, setActiveMode] = useState('wallet');
  const [employeeCount, setEmployeeCount] = useState('1');
  const [showModeInfo, setShowModeInfo] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [confirmationData, setConfirmationData] = useState(null);
  const [showFinalStatus, setShowFinalStatus] = useState(false);
  const [statusTimer, setStatusTimer] = useState(40);
  const pollingIntervalRef = useRef(null);
  const statusTimerRef = useRef(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentStats, setPaymentStats] = useState({ totalMonth:0, totalYear:0, totalTransactions:0 });
  const [loadingStats, setLoadingStats] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const navigate = useNavigate();
  const [registrationForm, setRegistrationForm] = useState({ firstName:'', lastName:'', phone:'', network:'', amount:'' });
  const [paymentForm, setPaymentForm] = useState({ amount:'' });
  
  // Custom prompt states
  const [prompt, setPrompt] = useState({ show: false, message: '', type: 'info' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeletePayer, setPendingDeletePayer] = useState(null);
  const [isDeletingPayer, setIsDeletingPayer] = useState(false);

  const computedAmount = activeMode !== 'wallet' ? calculateAmount(activeMode, employeeCount) : null;
  const SIDEBAR_WIDTH = 290;
  const TOPBAR_HEIGHT = 66;
  const mInfo = MODE_META[activeMode];

  // Helper to show custom prompts
  const showPrompt = (message, type = 'info') => {
    setPrompt({ show: true, message, type });
  };

  // ── Lifecycle ────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        if (!token) throw new Error('No token provided');
        const resp = await fetch(urls.fetchpaymentdetails, {
          method:'POST', headers:{'Content-Type':'application/json'},
          body:JSON.stringify({ token }),
        });
        if (!resp.ok) throw new Error('Failed to fetch clinic details');
        const data = await resp.json();
        const ci = countryData[data.country] || countryData['Uganda'];
        setClinicDetails({
          healthFacilityName: data.clinic_name || '',
          country: data.country || 'Uganda', countryIso: ci.iso,
          district: data.district || '', town: data.town || '', subCounty: data.sub_county || '',
          currentBalance: data.current_balance || 0, currency: ci.currency, token, modeAmount:'0',
        });
        await fetchAllowedPayers(token);
        await fetchPaymentData(token);
      } catch {
        setErrors({ general:'Failed to load clinic details. Please check your link or contact support.' });
        setTimeout(() => navigate('/login'), 3000);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  useEffect(() => () => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    if (statusTimerRef.current) clearInterval(statusTimerRef.current);
  }, []);

  useEffect(() => {
    if (showFinalStatus && statusTimer > 0) {
      statusTimerRef.current = setInterval(() => {
        setStatusTimer(prev => {
          if (prev <= 1) { clearInterval(statusTimerRef.current); window.location.reload(); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (statusTimerRef.current) clearInterval(statusTimerRef.current); };
  }, [showFinalStatus, statusTimer]);

  useEffect(() => {
    if (activeMode !== 'wallet' && computedAmount !== null) {
      const s = String(computedAmount);
      setPaymentForm(p => ({ ...p, amount:s }));
      setRegistrationForm(p => ({ ...p, amount:s }));
    }
  }, [activeMode, employeeCount, computedAmount]);

  // ── API ──────────────────────────────────────────────────────────────────────
  const fetchPaymentData = async (token) => {
    try {
      setLoadingStats(true);
      const resp = await fetch(urls.paymentsdata, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ token }),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.success) {
        if (Array.isArray(data.charges))
          setPaymentHistory([...data.charges].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)));
        setPaymentStats({ totalMonth:data.total_payments_this_month||0, totalYear:data.total_payments_this_year||0, totalTransactions:data.total_records||0 });
        if (data.clinic) setClinicDetails(p=>({...p, healthFacilityName:data.clinic}));
        const clean = (data.mode_amount||'0').split('.')[0];
        setClinicDetails(p=>({...p, modeAmount:clean}));
        if (clean && clean!=='0') setPaymentForm(p=>({...p, amount:clean}));
      }
    } finally { setLoadingStats(false); }
  };

  const fetchAllowedPayers = async (token) => {
    try {
      setFetchingPayers(true);
      const resp = await fetch(urls.fetchallowedpayers, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ token }),
      });
      if (!resp.ok) { setPayers([]); return; }
      const data = await resp.json();
      if (Array.isArray(data.payment_information)) {
        setPayers(data.payment_information.map((p, idx) => ({
          id:p.customer_id, customer_id:p.customer_id, email:p.email,
          displayName:`${p.first_name} ${p.middle_name||''} ${p.last_name||''}`.trim(),
          first_name:p.first_name, middle_name:p.middle_name, last_name:p.last_name,
          phone_country:p.phone_country, phone_number:p.phone_number,
          network:p.network||p.payment_methods?.[0]?.network||'',
          status:'active', country_code:p.phone_country||'256',
          phoneLabel: `Phone Number ${idx + 1}` // Generate sequential phone label
        })));
      } else setPayers([]);
    } catch { setPayers([]); }
    finally { setFetchingPayers(false); }
  };

  // --- Delete Payer Handler with enhanced loading states ---
  const handleDeletePayer = async () => {
    if (!pendingDeletePayer) return;
    
    const payer = pendingDeletePayer;
    setShowDeleteConfirm(false);
    setIsDeletingPayer(true);
    
    try {
      // Prepare payload with token and full payment details
      const payload = {
        token: clinicDetails.token,
        payer_details: {
          customer_id: payer.customer_id,
          phone_number: payer.phone_number,
          phone_country: payer.phone_country,
          network: payer.network,
          email: payer.email,
          first_name: payer.first_name,
          last_name: payer.last_name,
          display_name: payer.displayName
        },
        payment_mode_details: {
          active_mode: activeMode,
          employee_count: employeeCount,
          calculated_amount: computedAmount
        },
        timestamp: new Date().toISOString()
      };

      const resp = await fetch(urls.deletepayer, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText || 'Failed to delete payer');
      }

      const result = await resp.json();
      
      // Handle the success response structure
      if (result.status === 'success' && result.message === 'Customer record deleted successfully') {
        // Remove the payer from local state
        setPayers(prevPayers => prevPayers.filter(p => p.customer_id !== payer.customer_id));
        
        // Show success prompt with details from response
        const deletedInfo = result.data.deleted_record;
        const employeeInfo = result.data.employee;
        showPrompt(
          `✅ ${result.message}\n\nPayer: ${deletedInfo.name || deletedInfo.email}\nDeleted by: ${employeeInfo.name} (${employeeInfo.role})\nRows deleted: ${result.data.rows_deleted}`,
          'success'
        );
        
        // If the deleted payer was selected, clear selection
        if (selectedPayer?.customer_id === payer.customer_id) {
          setSelectedPayer(null);
        }
      } else {
        throw new Error(result.message || 'Deletion failed');
      }
    } catch (err) {
      console.error('Delete payer error:', err);
      showPrompt(`Failed to delete payer: ${err.message}`, 'error');
    } finally {
      setIsDeletingPayer(false);
      setPendingDeletePayer(null);
    }
  };

  const confirmPayment = async (chargeId) => {
    try {
      const resp = await fetch(urls.ConfirmPayment, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ charge_id:chargeId }),
      });
      if (!resp.ok) throw new Error();
      const data = await resp.json();
      let chargeStatus=null, chargeData=null;
      if (data.charge?.charge_response) {
        chargeData=data.charge.charge_response.response.data; chargeStatus=chargeData?.status;
      } else if (data.charge_details) {
        chargeData=data.charge_details; chargeStatus=data.charge_details.status;
      } else if (data.data) {
        chargeData=data.data; chargeStatus=data.data.status;
      } else { chargeStatus=data.status; chargeData=data; }
      if (chargeStatus==='succeeded') await fetchPaymentData(clinicDetails.token);
      return { ...data, charge_details:chargeData, status:chargeStatus };
    } catch { return null; }
  };

  const startPaymentPolling = (chargeId, initial=null) => {
    setIsPolling(true); setPollingAttempts(0);
    if (initial) {
      const s=initial.status;
      if (s==='succeeded'||s==='failed') { handleConfirmationResponse({charge_details:initial,status:s},chargeId); return; }
    }
    setTimeout(async()=>{ const r=await confirmPayment(chargeId); handleConfirmationResponse(r,chargeId); },10000);
  };

  const scheduleNextPoll = (chargeId) => {
    pollingIntervalRef.current=setTimeout(async()=>{ const r=await confirmPayment(chargeId); handleConfirmationResponse(r,chargeId); },10000);
  };

  const handleConfirmationResponse = (data, chargeId) => {
    if (!data) { setPollingAttempts(p=>p+1); scheduleNextPoll(chargeId); return; }
    const status=data.charge_details?.status||data.status;
    const chargeData=data.charge_details||data;
    if (status==='succeeded'||status==='failed') {
      setConfirmationData({...data, charge_details:chargeData, status, clinic_balance_update:data.clinic_balance_update||chargeData?.balance_update});
      setIsPolling(false); setShowFinalStatus(true); setStatusTimer(40);
      if (pollingIntervalRef.current){clearInterval(pollingIntervalRef.current);pollingIntervalRef.current=null;}
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
      statusTimerRef.current=setTimeout(()=>window.location.reload(),40000);
    } else if (pollingAttempts<12) {
      setPollingAttempts(p=>p+1); scheduleNextPoll(chargeId);
    } else {
      setConfirmationData({charge_details:chargeData,status:'timeout'});
      setIsPolling(false); setShowFinalStatus(true);
    }
  };

  const buildPaymentModePayload = () => {
    if (activeMode==='wallet') return {payment_mode:'wallet',employees_description:'unlimited'};
    const isAbove10=employeeCount==='above10';
    return {
      payment_mode:activeMode,
      employee_count:isAbove10?'above10':parseInt(employeeCount),
      employees_description:isAbove10?'above 10 employees':`${employeeCount} employee${parseInt(employeeCount)>1?'s':''}`,
      calculated_amount:computedAmount,
    };
  };

  const handleSelectPayerForPayment = (payer) => {
    setSelectedPayer(payer);
    const amount=computedAmount!==null?String(computedAmount):(clinicDetails.modeAmount&&clinicDetails.modeAmount!=='0'?clinicDetails.modeAmount:'');
    setPaymentForm({amount});
  };

  const handleBackToSelection = () => {
    setSelectedPayer(null); setShowNewPayerForm(false);
    setPaymentForm({amount:''}); setConfirmationData(null);
    setIsPolling(false); setPollingAttempts(0); setShowFinalStatus(false); setStatusTimer(40);
    setErrors({}); setNetworkWarning(''); setForceNetworkConfirmation(false);
    if (pollingIntervalRef.current){clearInterval(pollingIntervalRef.current);pollingIntervalRef.current=null;}
    if (statusTimerRef.current){clearInterval(statusTimerRef.current);statusTimerRef.current=null;}
  };

  const validateRegistrationForm = () => {
    const errs={};
    if (!registrationForm.firstName||registrationForm.firstName.trim().length<2) errs.firstName='First name required (min 2 chars).';
    if (!registrationForm.lastName ||registrationForm.lastName.trim().length <2) errs.lastName ='Last name required (min 2 chars).';
    if (!registrationForm.phone) {
      errs.phone='Phone number is required.';
    } else {
      const ci=countryData[clinicDetails.country]||countryData['Uganda'];
      const digits=registrationForm.phone.replace(/\D/g,'');
      if (!digits.startsWith(ci.code)) errs.phone=`Must start with +${ci.code}`;
      else if (digits.length!==ci.code.length+9) errs.phone=`Must have exactly 9 digits after +${ci.code}`;
      else if (ci.code==='256'&&!digits.substring(ci.code.length).startsWith('7')) errs.phone='Uganda numbers must start with 7 after +256';
    }
    if (!registrationForm.network) errs.network='Please select a mobile network.';
    if (activeMode==='wallet'&&(!registrationForm.amount||parseFloat(registrationForm.amount)<=0)) errs.amount='Please enter a valid amount.';
    return errs;
  };

  const handleNewPayerRegistration = async (e) => {
    e.preventDefault();
    if (networkWarning&&!forceNetworkConfirmation){setErrors({network:'Please confirm your network selection.'});return;}
    const errs=validateRegistrationForm();
    if (Object.keys(errs).length>0){setErrors(errs);return;}
    try {
      setLoading(true); setErrors({});
      const ci=countryData[clinicDetails.country]||countryData['Uganda'];
      let phone=registrationForm.phone.trim();
      if (!phone.startsWith('+')) phone=`+${phone}`;
      const cc=`+${ci.code}`;
      const userNumber=phone.startsWith(cc)?phone.substring(cc.length):phone;
      if (userNumber.length!==9) throw new Error(`Phone must have exactly 9 digits after ${cc}.`);
      const cleanAmount=(computedAmount!==null?String(computedAmount):registrationForm.amount).split('.')[0];
      const payload={
        token:clinicDetails.token, currency:clinicDetails.currency,
        clinicName:clinicDetails.healthFacilityName,
        first_name:registrationForm.firstName.trim(), last_name:registrationForm.lastName.trim(),
        network:registrationForm.network.toUpperCase(),
        phone:{country_code:ci.code,number:userNumber},
        amount:parseFloat(cleanAmount),
        ...buildPaymentModePayload(),
      };
      const resp=await fetch(urls.flutter_submit2,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
      if (!resp.ok) throw new Error(`Server error: ${await resp.text()}`);
      const data=await resp.json();
      if (data.success&&data.charge?.charge_response) {
        const cd=data.charge.charge_response.response.data;
        if (cd?.id){setView('paymentSuccess');startPaymentPolling(cd.id,cd);setConfirmationData({charge_details:cd,status:cd.status,charge_id:cd.id});}
        else throw new Error('No charge ID returned');
      } else if (data.success||data.status==='success'||data.status==='pending') {
        const id=data.charge_id||data.id;
        if (id){setView('paymentSuccess');startPaymentPolling(id);}
        else throw new Error('No charge ID returned');
      } else throw new Error(data.message||data.msg||'Registration or payment failed');
    } catch(err) {
      setErrors({general:err.message||'Something went wrong. Please contact the technical team.'});
    } finally { setLoading(false); }
  };

  const handleChargePayment = async (e) => {
    e.preventDefault();
    const amount=computedAmount!==null?computedAmount:parseFloat(paymentForm.amount);
    if (!amount||amount<=0){setErrors({amount:'Please enter a valid amount.'});return;}
    try {
      setLoading(true); setErrors({});
      const payload={
        token:clinicDetails.token, customer_id:selectedPayer.customer_id,
        amount:Math.round(amount), network:selectedPayer?.network?.toUpperCase()||'',
        currency:clinicDetails.currency, clinicName:clinicDetails.healthFacilityName,
        ...buildPaymentModePayload(),
      };
      const resp=await fetch(urls.chargeCustomer,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
      if (!resp.ok) throw new Error(`Server error: ${await resp.text()}`);
      const data=await resp.json();
      if (data.success&&data.charge?.charge_response) {
        const cd=data.charge.charge_response.response.data;
        if (cd?.id){setView('paymentSuccess');startPaymentPolling(cd.id,cd);setConfirmationData({charge_details:cd,status:cd.status,charge_id:cd.id});}
        else throw new Error('No charge ID returned');
      } else if (data.success||data.status==='success') {
        const id=data.data?.id||data.id;
        if (id){setView('paymentSuccess');startPaymentPolling(id);}
        else throw new Error('No charge ID returned');
      } else throw new Error(data.message||data.msg||'Payment failed');
    } catch(err){setErrors({general:err.message||'Error processing payment.'});}
    finally{setLoading(false);}
  };

  const handleRegistrationInputChange = (e) => {
    const {name,value}=e.target; let v=value;
    if (name==='amount') {
      v=value.replace(/\D/g,'');
    } else if (name==='phone') {
      const ci=countryData[clinicDetails.country]||countryData['Uganda'];
      v=value.replace(/\D/g,'');
      if (!v.startsWith(ci.code)) v=ci.code+v;
      v=`+${v.substring(0,ci.code.length+9)}`;
      if (v.length>=6&&registrationForm.network){const{warning}=validateNetworkPrefix(v,registrationForm.network);setNetworkWarning(warning);setForceNetworkConfirmation(false);}
    } else if (name==='network') {
      if (registrationForm.phone){const{warning}=validateNetworkPrefix(registrationForm.phone,value);setNetworkWarning(warning);setForceNetworkConfirmation(false);}
    }
    setRegistrationForm(p=>({...p,[name]:v}));
    if (errors[name]) setErrors(p=>({...p,[name]:''}));
  };

  const handlePaymentInputChange = (e) => {
    const {name,value}=e.target;
    setPaymentForm(p=>({...p,[name]:name==='amount'?value.replace(/\D/g,''):value}));
    if (errors[name]) setErrors(p=>({...p,[name]:''}));
  };

  const formatDate = (ds) => new Date(ds).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'});

  // ── Shared styles ────────────────────────────────────────────────────────────
  const inputStyle = (hasError) => ({
    width:'100%', padding:'11px 15px',
    border:`1.5px solid ${hasError?'#ef4444':'#e2e8f0'}`,
    borderRadius:'10px', fontSize:'14px', outline:'none',
    fontFamily:"'DM Sans',system-ui,sans-serif",
    boxSizing:'border-box', color:'#0f172a', background:'#fff',
    transition:'border-color 0.15s ease',
  });
  const labelStyle = { display:'block', marginBottom:'6px', fontWeight:'700', color:'#334155', fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.7px' };
  const errStyle   = { color:'#ef4444', fontSize:'11px', marginTop:'4px', display:'block' };

  // ════════════════════════════════════════════════════════════════════════════
  // SIDEBAR
  // ════════════════════════════════════════════════════════════════════════════
  const renderSidebar = () => (
    <div style={{ width:`${SIDEBAR_WIDTH}px`, background:T.sidebar, borderRight:`1px solid ${T.sidebarBorder}`, padding:'28px 22px', display:'flex', flexDirection:'column', height:'100vh', position:'fixed', left:0, top:0, overflowY:'auto', zIndex:100, fontFamily:"'DM Sans',system-ui,sans-serif" }}>

      {/* Logo */}
      <div style={{ marginBottom:'28px', paddingBottom:'22px', borderBottom:`1px solid ${T.sidebarBorder}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:'11px' }}>
          <div style={{ width:'40px', height:'40px', background:T.logoGrad, borderRadius:'12px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'20px', boxShadow:'0 4px 12px rgba(0,0,0,0.15)', flexShrink:0 }}>💊</div>
          <div>
            <h2 style={{ fontSize:'15px', fontWeight:'800', margin:0, color:T.sidebarText, letterSpacing:'-0.3px', fontFamily:"'DM Sans',system-ui,sans-serif" }}>ClinicProPay</h2>
            <p style={{ fontSize:'10px', color:T.sidebarMuted, margin:'1px 0 0', letterSpacing:'0.5px', fontWeight:'500' }}>SECURE HEALTHCARE PAYMENTS</p>
          </div>
        </div>
      </div>

      {/* Facility */}
      <div style={{ marginBottom:'18px', padding:'14px 16px', background:T.sidebarCard, borderRadius:'14px', border:`1px solid ${T.sidebarCardBorder}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'9px' }}>
          <FontAwesomeIcon icon={faHospital} style={{ color:T.sidebarAccent, fontSize:'11px' }} />
          <span style={{ fontSize:'9px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'1px', color:T.sidebarMuted }}>Facility</span>
        </div>
        <h3 style={{ fontSize:'14px', fontWeight:'700', margin:'0 0 5px', color:T.sidebarText, lineHeight:'1.3' }}>{clinicDetails.healthFacilityName||'—'}</h3>
        <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
          <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize:'9px', color:T.sidebarMuted }} />
          <span style={{ fontSize:'10px', color:T.sidebarMuted }}>{[clinicDetails.district,clinicDetails.country].filter(Boolean).join(', ')}</span>
        </div>
      </div>

      {/* Balance */}
      <div style={{ marginBottom:'18px', padding:'18px', background:T.balanceBg, borderRadius:'14px', border:`1px solid ${T.balanceBorder}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'10px' }}>
          <FontAwesomeIcon icon={faMoneyBillWave} style={{ color:T.sidebarAccent, fontSize:'12px' }} />
          <span style={{ fontSize:'9px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'1px', color:isDarkSidebar?'rgba(255,255,255,0.5)':T.sidebarMuted }}>Wallet Balance</span>
        </div>
        <p style={{ fontSize:'30px', fontWeight:'800', margin:0, color:T.balanceText, lineHeight:1 }}>{clinicDetails.currentBalance.toLocaleString()}</p>
        <p style={{ fontSize:'12px', color:isDarkSidebar?'rgba(255,255,255,0.4)':T.sidebarMuted, margin:'4px 0 0', fontWeight:'500' }}>{clinicDetails.currency}</p>
      </div>

      {/* Stats */}
      <div style={{ marginBottom:'18px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'10px' }}>
          <FontAwesomeIcon icon={faChartLine} style={{ color:T.sidebarAccent, fontSize:'11px' }} />
          <span style={{ fontSize:'9px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'1px', color:T.sidebarMuted }}>Statistics</span>
        </div>
        {loadingStats ? (
          <div style={{ textAlign:'center', padding:'14px' }}><FontAwesomeIcon icon={faSpinner} spin style={{ color:T.sidebarAccent }} /></div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
            {[{label:'This Month',value:paymentStats.totalMonth},{label:'This Year',value:paymentStats.totalYear}].map((s,i)=>(
              <div key={i} style={{ background:T.sidebarCard, border:`1px solid ${T.sidebarCardBorder}`, borderRadius:'10px', padding:'11px 12px' }}>
                <p style={{ fontSize:'9px', fontWeight:'600', textTransform:'uppercase', color:T.sidebarMuted, margin:'0 0 5px', letterSpacing:'0.5px' }}>{s.label}</p>
                <p style={{ fontSize:'16px', fontWeight:'800', margin:0, color:T.sidebarText }}>{s.value.toLocaleString()}</p>
                <p style={{ fontSize:'9px', color:T.sidebarMuted, margin:'1px 0 0' }}>{clinicDetails.currency}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent */}
      <div style={{ flex:1, minHeight:0 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
            <FontAwesomeIcon icon={faHistory} style={{ color:T.sidebarAccent, fontSize:'11px' }} />
            <span style={{ fontSize:'9px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'1px', color:T.sidebarMuted }}>Recent</span>
          </div>
          {paymentHistory.length>5&&(
            <button onClick={()=>setShowAllTransactions(p=>!p)} style={{ padding:'3px 8px', background:T.sidebarCard, color:T.sidebarMuted, border:`1px solid ${T.sidebarCardBorder}`, borderRadius:'6px', fontSize:'9px', fontWeight:'600', cursor:'pointer' }}>
              {showAllTransactions?'Less':'See All'}
            </button>
          )}
        </div>
        {loadingStats?(<div style={{textAlign:'center',padding:'14px'}}><FontAwesomeIcon icon={faSpinner} spin style={{color:T.sidebarAccent}}/></div>)
        :paymentHistory.length===0?(<div style={{textAlign:'center',padding:'16px',background:T.sidebarCard,borderRadius:'10px',border:`1px solid ${T.sidebarCardBorder}`}}><p style={{fontSize:'11px',color:T.sidebarMuted,margin:0}}>No payments yet</p></div>)
        :(
          <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
            {(showAllTransactions?paymentHistory.slice(0,10):paymentHistory.slice(0,5)).map((p,i)=>(
              <div key={i} style={{ background:T.sidebarCard, border:`1px solid ${T.sidebarCardBorder}`, borderRadius:'9px', padding:'9px 12px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'3px' }}>
                  <span style={{ fontSize:'9px', fontWeight:'700', padding:'2px 7px', borderRadius:'20px', background:p.status==='succeeded'?'rgba(34,197,94,0.15)':p.status==='failed'?'rgba(239,68,68,0.15)':'rgba(245,158,11,0.15)', color:p.status==='succeeded'?'#4ade80':p.status==='failed'?'#f87171':'#fbbf24' }}>
                    {p.status==='succeeded'?'✓ OK':p.status==='failed'?'✗ FAIL':'⏱ …'}
                  </span>
                  <span style={{ fontSize:'13px', fontWeight:'800', color:T.sidebarText }}>
                    {parseInt(p.amount||0).toLocaleString()}<span style={{ fontSize:'9px', fontWeight:'400', color:T.sidebarMuted }}> {clinicDetails.currency}</span>
                  </span>
                </div>
                <div style={{ fontSize:'9px', color:T.sidebarMuted }}>{formatDate(p.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ marginTop:'16px', paddingTop:'16px', borderTop:`1px solid ${T.sidebarBorder}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
          <FontAwesomeIcon icon={faShieldAlt} style={{ color:T.sidebarAccent, fontSize:'10px' }} />
          <span style={{ fontSize:'10px', color:T.sidebarMuted }}>All transactions encrypted &amp; secure</span>
        </div>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // TOP BAR
  // ════════════════════════════════════════════════════════════════════════════
  const renderTopBar = () => (
    <div style={{ position:'fixed', top:0, left:`${SIDEBAR_WIDTH}px`, right:0, height:`${TOPBAR_HEIGHT}px`, background:T.topbar, borderBottom:`1px solid ${T.topbarBorder}`, zIndex:90, display:'flex', alignItems:'center', paddingLeft:'32px', paddingRight:'24px', gap:'8px', overflowX:'auto' }}>
      {PAYMENT_MODES.map(mode=>{
        const isActive=activeMode===mode.id;
        const info=MODE_META[mode.id];
        return (
          <button key={mode.id} onClick={()=>{setActiveMode(mode.id);setShowModeInfo(false);}} style={{ display:'flex', alignItems:'center', gap:'7px', padding:'8px 18px', borderRadius:'40px', border:isActive?`1.5px solid ${info.color}`:`1.5px solid ${T.topbarBorder||'rgba(255,255,255,0.12)'}`, background:isActive?info.bgColor:'transparent', color:isActive?info.color:(isDarkSidebar?'#94a3b8':'#64748b'), fontSize:'13px', fontWeight:isActive?'700':'500', cursor:'pointer', whiteSpace:'nowrap', fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all 0.2s ease', flexShrink:0 }}>
            <FontAwesomeIcon icon={mode.icon} style={{ fontSize:'12px' }} />
            {mode.label}
            {mode.id==='wallet'&&isActive&&(<span style={{ width:'7px', height:'7px', borderRadius:'50%', background:'#10b981', display:'inline-block' }}/>)}
          </button>
        );
      })}
      <button onClick={()=>setShowModeInfo(p=>!p)} style={{ marginLeft:'auto', flexShrink:0, display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'40px', border:`1.5px solid ${T.topbarBorder||'rgba(255,255,255,0.12)'}`, background:showModeInfo?(isDarkSidebar?'rgba(255,255,255,0.1)':'#f1f5f9'):'transparent', color:isDarkSidebar?'#94a3b8':'#64748b', fontSize:'12px', fontWeight:'600', cursor:'pointer', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
        <FontAwesomeIcon icon={faInfoCircle}/> Info
      </button>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // MODE INFO PANEL
  // ════════════════════════════════════════════════════════════════════════════
  const renderModeInfoPanel = () => {
    if (!showModeInfo) return null;
    const info=mInfo;
    return (
      <div style={{ background:T.cardBg, border:`1.5px solid ${info.borderColor}`, borderRadius:'18px', padding:'24px', marginBottom:'24px', position:'relative', boxShadow:`0 8px 32px ${info.bgColor}`, overflow:'hidden' }}>
        <div style={{ position:'absolute', top:0, left:0, right:0, height:'4px', background:`linear-gradient(90deg,${info.gradStart},${info.gradEnd})`, borderRadius:'18px 18px 0 0' }}/>
        <button onClick={()=>setShowModeInfo(false)} style={{ position:'absolute', top:'16px', right:'16px', background:'#f1f5f9', border:'none', cursor:'pointer', color:'#64748b', fontSize:'12px', padding:'6px 8px', borderRadius:'8px', lineHeight:1 }}>
          <FontAwesomeIcon icon={faTimes}/>
        </button>
        <div style={{ display:'flex', alignItems:'center', gap:'14px', marginBottom:'14px' }}>
          <div style={{ width:'52px', height:'52px', borderRadius:'14px', background:`linear-gradient(135deg,${info.gradStart},${info.gradEnd})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'24px', flexShrink:0, boxShadow:`0 4px 16px ${info.bgColor}` }}>
            {info.emoji}
          </div>
          <div>
            <h4 style={{ margin:0, fontSize:'18px', fontWeight:'800', color:'#0f172a', fontFamily:"'DM Sans',system-ui,sans-serif", letterSpacing:'-0.3px' }}>{info.title}</h4>
            <p style={{ margin:'2px 0 0', fontSize:'12px', color:info.color, fontWeight:'600' }}>{info.tagline}</p>
          </div>
        </div>
        <p style={{ fontSize:'13px', color:'#475569', margin:'0 0 18px', lineHeight:'1.65' }}>{info.description}</p>

        {/* Pricing table */}
        <div style={{ borderRadius:'12px', overflow:'hidden', border:`1px solid ${info.borderColor}`, marginBottom:'14px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', padding:'9px 16px', background:`linear-gradient(90deg,${info.gradStart}22,${info.gradEnd}22)` }}>
            <span style={{ fontSize:'10px', fontWeight:'800', color:info.color, textTransform:'uppercase', letterSpacing:'0.8px' }}>Tier</span>
            <span style={{ fontSize:'10px', fontWeight:'800', color:info.color, textTransform:'uppercase', letterSpacing:'0.8px' }}>Amount</span>
          </div>
          {info.rows&&info.rows.map((row,i)=>(
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'11px 16px', background:row.highlight?info.bgColor:(i%2===0?'#fafafa':'#fff'), borderTop:`1px solid ${info.borderColor}` }}>
              <span style={{ fontSize:'12px', color:'#64748b', fontWeight:'500' }}>{row.label}</span>
              <span style={{ fontSize:'13px', fontWeight:row.highlight?'800':'600', color:row.highlight?info.color:'#0f172a', fontFamily:"'DM Sans',system-ui,sans-serif" }}>{row.value}</span>
            </div>
          ))}
        </div>

        <div style={{ background:info.bgColor, borderRadius:'10px', padding:'12px 16px', display:'flex', alignItems:'flex-start', gap:'8px' }}>
          <span style={{ fontSize:'14px' }}>💡</span>
          <p style={{ fontSize:'12px', color:info.color, fontWeight:'600', margin:0, lineHeight:'1.5' }}>{info.note}</p>
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // EMPLOYEE SELECTOR
  // ════════════════════════════════════════════════════════════════════════════
  const renderEmployeeSelector = () => {
    if (activeMode==='wallet') return null;
    const info=mInfo;
    const options=[
      ...Array.from({length:10},(_,i)=>({value:String(i+1),label:`${i+1} Employee${i>0?'s':''}`})),
      {value:'above10',label:'Above 10 (Flat Rate)'},
    ];
    const count=parseInt(employeeCount);
    const hasDiscount=!isNaN(count)&&count>=3&&employeeCount!=='above10';
    return (
      <div style={{ background:T.cardBg, border:`1.5px solid ${info.borderColor}`, borderRadius:'16px', padding:'18px 22px', marginBottom:'24px', display:'flex', alignItems:'center', gap:'24px', flexWrap:'wrap', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px', flex:'1 1 200px' }}>
          <div style={{ width:'42px', height:'42px', borderRadius:'12px', background:info.bgColor, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <FontAwesomeIcon icon={faUsers} style={{ color:info.color, fontSize:'16px' }}/>
          </div>
          <div>
            <p style={{ margin:'0 0 5px', fontSize:'11px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.8px' }}>Number of Employees</p>
            <select value={employeeCount} onChange={e=>setEmployeeCount(e.target.value)} style={{ padding:'9px 14px', border:`1.5px solid ${info.borderColor}`, borderRadius:'9px', fontSize:'14px', fontWeight:'700', color:'#0f172a', background:info.bgColor, cursor:'pointer', fontFamily:"'DM Sans',system-ui,sans-serif", outline:'none', minWidth:'200px' }}>
              {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        {computedAmount!==null&&(
          <div style={{ textAlign:'right', flex:'0 0 auto' }}>
            <p style={{ margin:0, fontSize:'10px', color:'#94a3b8', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.6px' }}>
              Calculated Amount{hasDiscount&&<span style={{ color:'#10b981', marginLeft:'5px' }}>(10% off)</span>}
            </p>
            <p style={{ margin:'3px 0 0', fontSize:'30px', fontWeight:'800', color:info.color, lineHeight:1 }}>{computedAmount.toLocaleString()}</p>
            <p style={{ margin:'2px 0 0', fontSize:'12px', color:'#94a3b8', fontWeight:'500' }}>{clinicDetails.currency}</p>
          </div>
        )}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // PAYER SELECTION (Modified: hidden names/emails, sequential phone labels, delete button)
  // ════════════════════════════════════════════════════════════════════════════
  const renderPayerSelection = () => {
    if (selectedPayer)    return renderPaymentForm();
    if (showNewPayerForm) return renderNewPayerForm();
    return (
      <div>
        {renderModeInfoPanel()}
        {renderEmployeeSelector()}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'28px', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <h1 style={{ fontSize:'26px', fontWeight:'800', color:'#0f172a', letterSpacing:'-0.5px', fontFamily:"'DM Sans',system-ui,sans-serif", margin:'0 0 4px' }}>Select Payer</h1>
            <p style={{ color:'#64748b', fontSize:'13px', margin:0 }}>Choose a registered payer or add a new one</p>
          </div>
          <button onClick={()=>setShowNewPayerForm(true)} style={{ padding:'11px 22px', background:T.successGrad, color:'#fff', border:'none', borderRadius:'12px', fontSize:'13px', fontWeight:'700', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px', boxShadow:'0 4px 16px rgba(16,185,129,0.3)', fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all 0.15s ease' }}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 8px 24px rgba(16,185,129,0.4)';}}
            onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 4px 16px rgba(16,185,129,0.3)';}}>
            <FontAwesomeIcon icon={faUserPlus}/> Use New Number
          </button>
        </div>

        {fetchingPayers?(
          <div style={{ textAlign:'center', padding:'80px 20px' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize:'36px', color:T.primary }}/>
            <p style={{ marginTop:'16px', color:'#64748b', fontSize:'14px' }}>Loading payers…</p>
          </div>
        ):payers.length===0?(
          <div style={{ textAlign:'center', padding:'80px 20px', maxWidth:'380px', margin:'0 auto' }}>
            <div style={{ width:'80px', height:'80px', background:'linear-gradient(135deg,#e0e7ff,#c7d2fe)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
              <FontAwesomeIcon icon={faUser} style={{ fontSize:'34px', color:'#4f46e5' }}/>
            </div>
            <h3 style={{ fontSize:'20px', fontWeight:'700', color:'#0f172a', marginBottom:'8px', fontFamily:"'DM Sans',system-ui,sans-serif" }}>No Payers Found</h3>
            <p style={{ color:'#64748b', marginBottom:'24px', fontSize:'14px' }}>Get started by registering a new payer</p>
            <button onClick={()=>setShowNewPayerForm(true)} style={{ padding:'12px 24px', background:T.successGrad, color:'#fff', border:'none', borderRadius:'12px', fontSize:'14px', fontWeight:'700', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px', margin:'0 auto', boxShadow:'0 4px 16px rgba(16,185,129,0.3)' }}>
              <FontAwesomeIcon icon={faUserPlus}/> Register New Number
            </button>
          </div>
        ):(
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:'16px' }}>
            {payers.map(payer=>(
              <div key={payer.customer_id} style={{ background:T.cardBg, border:`1.5px solid ${T.cardBorder}`, borderRadius:'16px', padding:'20px', transition:'all 0.22s ease', boxShadow:'0 1px 4px rgba(0,0,0,0.05)', position:'relative' }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=T.primary;e.currentTarget.style.boxShadow='0 8px 28px rgba(59,130,246,0.14)';e.currentTarget.style.transform='translateY(-3px)';}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.cardBorder;e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.05)';e.currentTarget.style.transform='none';}}>
                
                {/* Delete Button - Top Right */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingDeletePayer(payer);
                    setShowDeleteConfirm(true);
                  }}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    color: '#ef4444',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    transition: 'all 0.2s ease',
                    zIndex: 10
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = 'white'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; e.currentTarget.style.color = '#ef4444'; }}
                >
                  <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: '11px' }} />
                  <span style={{ fontSize: '10px' }}>Delete</span>
                </button>

                {/* Clickable area for payment selection */}
                <div onClick={() => handleSelectPayerForPayment(payer)} style={{ cursor: 'pointer' }}>
                  <div style={{ display:'flex', alignItems:'flex-start', marginBottom:'16px', paddingRight:'50px' }}>
                    {/* Anonymous Avatar - No initials/names shown */}
                    <div style={{ width:'48px', height:'48px', borderRadius:'14px', background:T.primaryGrad, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', marginRight:'13px', fontSize:'20px', fontWeight:'800', flexShrink:0 }}>
                      <FontAwesomeIcon icon={faUser} style={{ fontSize: '20px' }} />
                    </div>
                    <div style={{ flex:1 }}>
                      {/* Hidden Name - Just show phone label */}
                      <h4 style={{ fontSize:'15px', fontWeight:'700', color:'#0f172a', margin:'0 0 4px', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
                        {payer.phoneLabel || 'Phone Number'}
                      </h4>
                      {/* Phone number only - no email */}
                      <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                        <FontAwesomeIcon icon={faPhone} style={{ fontSize:'9px', color:'#94a3b8' }}/>
                        <span style={{ fontSize:'12px', color:'#64748b', fontWeight:'500' }}>
                          +{payer.phone_country}{payer.phone_number}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:'14px', borderTop:`1px solid ${T.cardBorder}` }}>
                    <span style={{ fontSize:'10px', fontWeight:'800', padding:'4px 12px', borderRadius:'20px', background:payer.network?.toLowerCase()==='mtn'?'#fef3c7':'#fee2e2', color:payer.network?.toLowerCase()==='mtn'?'#92400e':'#991b1b', letterSpacing:'0.5px' }}>
                      {payer.network?.toUpperCase()||'N/A'}
                    </span>
                    <span style={{ fontSize:'12px', color:T.primary, fontWeight:'700', display:'flex', alignItems:'center', gap:'5px' }}>
                      Pay Now <FontAwesomeIcon icon={faArrowRight} style={{ fontSize:'10px' }}/>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // NEW PAYER FORM (unchanged)
  // ════════════════════════════════════════════════════════════════════════════
  const renderNewPayerForm = () => {
    const info=mInfo;
    const isFormSubmitting = loading;
    return (
      <div style={{ maxWidth:'740px', margin:'0 auto' }}>
        <button onClick={()=>setShowNewPayerForm(false)} disabled={isFormSubmitting} style={{ display:'flex', alignItems:'center', gap:'7px', padding:'6px 0', background:'transparent', border:'none', color:isFormSubmitting?'#cbd5e1':'#64748b', fontSize:'13px', fontWeight:'600', cursor:isFormSubmitting?'not-allowed':'pointer', marginBottom:'20px', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
          <FontAwesomeIcon icon={faArrowLeft}/> Back to Payers
        </button>

        {renderModeInfoPanel()}
        {renderEmployeeSelector()}

        {networkWarning&&(
          <div style={{ background:'linear-gradient(135deg,#fef3c7,#fde68a)', border:'1px solid #f59e0b', borderRadius:'12px', padding:'16px', marginBottom:'20px' }}>
            <div style={{ display:'flex', gap:'12px' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ color:'#d97706', fontSize:'18px', flexShrink:0, marginTop:'1px' }}/>
              <div>
                <p style={{ color:'#92400e', fontSize:'13px', fontWeight:'700', margin:'0 0 5px' }}>Network Mismatch Warning</p>
                <p style={{ color:'#b45309', fontSize:'12px', margin:0 }}>{networkWarning}</p>
                <label style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'10px', cursor:'pointer' }}>
                  <input type="checkbox" checked={forceNetworkConfirmation} onChange={e=>setForceNetworkConfirmation(e.target.checked)} style={{ width:'14px', height:'14px' }}/>
                  <span style={{ color:'#92400e', fontSize:'12px', fontWeight:'600' }}>I confirm my network selection is correct</span>
                </label>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleNewPayerRegistration}>
          <div style={{ background:T.cardBg, borderRadius:'20px', padding:'28px', border:`1.5px solid ${T.cardBorder}`, boxShadow:'0 4px 16px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize:'16px', fontWeight:'800', color:'#0f172a', margin:'0 0 22px', fontFamily:"'DM Sans',system-ui,sans-serif", letterSpacing:'-0.3px' }}>New Payer Details</h3>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))', gap:'16px', marginBottom:'16px' }}>
              <div>
                <label style={labelStyle}>First Name *</label>
                <input type="text" name="firstName" value={registrationForm.firstName} onChange={handleRegistrationInputChange} style={inputStyle(errors.firstName)} placeholder="First name" disabled={isFormSubmitting}
                  onFocus={e=>e.target.style.borderColor=T.primary} onBlur={e=>e.target.style.borderColor=errors.firstName?'#ef4444':'#e2e8f0'}/>
                {errors.firstName&&<span style={errStyle}>{errors.firstName}</span>}
              </div>
              <div>
                <label style={labelStyle}>Last Name *</label>
                <input type="text" name="lastName" value={registrationForm.lastName} onChange={handleRegistrationInputChange} style={inputStyle(errors.lastName)} placeholder="Last name" disabled={isFormSubmitting}
                  onFocus={e=>e.target.style.borderColor=T.primary} onBlur={e=>e.target.style.borderColor=errors.lastName?'#ef4444':'#e2e8f0'}/>
                {errors.lastName&&<span style={errStyle}>{errors.lastName}</span>}
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))', gap:'16px', marginBottom:'20px' }}>
              <div>
                <label style={labelStyle}>Phone Number *</label>
                <input type="tel" name="phone" value={registrationForm.phone} onChange={handleRegistrationInputChange} style={inputStyle(errors.phone)} placeholder="+2567XXXXXXXX" disabled={isFormSubmitting}
                  onFocus={e=>e.target.style.borderColor=T.primary} onBlur={e=>e.target.style.borderColor=errors.phone?'#ef4444':'#e2e8f0'}/>
                <span style={{ fontSize:'10px', color:'#94a3b8', marginTop:'3px', display:'block' }}>9 digits after +256, starting with 7</span>
                {errors.phone&&<span style={errStyle}>{errors.phone}</span>}
              </div>
              <div>
                <label style={labelStyle}>Mobile Network *</label>
                <div style={{ display:'flex', gap:'10px' }}>
                  {['mtn','airtel'].map(net=>(
                    <label key={net} style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:'7px', padding:'11px', border:`1.5px solid ${registrationForm.network===net?T.primary:'#e2e8f0'}`, borderRadius:'10px', cursor:isFormSubmitting?'not-allowed':'pointer', background:registrationForm.network===net?'#eff6ff':'#fff', transition:'all 0.15s ease', fontSize:'13px', fontWeight:'700', color:registrationForm.network===net?T.primary:'#475569', opacity:isFormSubmitting?0.6:1 }}>
                      <input type="radio" name="network" value={net} checked={registrationForm.network===net} onChange={handleRegistrationInputChange} style={{ display:'none' }} disabled={isFormSubmitting}/>
                      {net==='mtn'?'🟡':'🔴'} {net.toUpperCase()}
                    </label>
                  ))}
                </div>
                {errors.network&&<span style={errStyle}>{errors.network}</span>}
              </div>
            </div>

            <div style={{ marginBottom:'24px' }}>
              <label style={labelStyle}>Amount ({clinicDetails.currency}) *</label>
              {activeMode!=='wallet'&&computedAmount!==null?(
                <div style={{ padding:'14px 18px', borderRadius:'12px', background:info.bgColor, border:`1.5px solid ${info.borderColor}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div>
                    <span style={{ fontSize:'12px', color:info.color, fontWeight:'700' }}>Auto-calculated</span>
                    <p style={{ margin:'1px 0 0', fontSize:'11px', color:'#94a3b8' }}>{PAYMENT_MODES.find(m=>m.id===activeMode)?.shortLabel}</p>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <span style={{ fontSize:'28px', fontWeight:'800', color:info.color, display:'block', lineHeight:1 }}>{computedAmount.toLocaleString()}</span>
                    <span style={{ fontSize:'11px', color:'#94a3b8', fontWeight:'500' }}>{clinicDetails.currency}</span>
                  </div>
                </div>
              ):(
                <>
                  <input type="text" name="amount" value={registrationForm.amount} onChange={handleRegistrationInputChange} style={{...inputStyle(errors.amount),fontSize:'20px',fontWeight:'700'}} placeholder="0" disabled={isFormSubmitting}
                    onFocus={e=>e.target.style.borderColor=T.primary} onBlur={e=>e.target.style.borderColor=errors.amount?'#ef4444':'#e2e8f0'}/>
                  {clinicDetails.modeAmount&&clinicDetails.modeAmount!=='0'&&(<span style={{ fontSize:'11px', color:'#f59e0b', marginTop:'3px', display:'block' }}>💡 Suggested: {parseInt(clinicDetails.modeAmount).toLocaleString()} {clinicDetails.currency}</span>)}
                  {errors.amount&&<span style={errStyle}>{errors.amount}</span>}
                </>
              )}
            </div>

            <button type="submit" disabled={isFormSubmitting || (networkWarning&&!forceNetworkConfirmation)} style={{ width:'100%', padding:'14px', background:(isFormSubmitting||(networkWarning&&!forceNetworkConfirmation))?'#cbd5e1':T.successGrad, color:'#fff', border:'none', borderRadius:'12px', fontSize:'15px', fontWeight:'800', cursor:isFormSubmitting?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'9px', fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:(isFormSubmitting||(networkWarning&&!forceNetworkConfirmation))?'none':'0 4px 16px rgba(16,185,129,0.3)', transition:'all 0.2s ease' }}>
              {isFormSubmitting?<><FontAwesomeIcon icon={faSpinner} spin/> Processing…</>:'💰 Register & Pay'}
            </button>
          </div>
        </form>

        <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:'14px', padding:'16px 20px', marginTop:'16px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'9px' }}><span>💡</span><h4 style={{ fontSize:'13px', fontWeight:'700', color:'#0369a1', margin:0 }}>Payment Instructions</h4></div>
          <div style={{ fontSize:'12px', color:'#0284c7', lineHeight:'1.7' }}>
            <p style={{ margin:'0 0 3px' }}>• You will be registered as a new payer automatically</p>
            <p style={{ margin:'0 0 3px' }}>• A payment prompt will be sent to your phone via mobile money</p>
            <p style={{ margin:0 }}>• Check your phone and approve the payment when prompted</p>
          </div>
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // PAYMENT FORM (existing payer) - (unchanged)
  // ════════════════════════════════════════════════════════════════════════════
  const renderPaymentForm = () => {
    const info=mInfo;
    const isPaymentSubmitting = loading;
    return (
      <div style={{ maxWidth:'580px', margin:'0 auto' }}>
        <button onClick={handleBackToSelection} disabled={isPaymentSubmitting} style={{ display:'flex', alignItems:'center', gap:'7px', padding:'6px 0', background:'transparent', border:'none', color:isPaymentSubmitting?'#cbd5e1':'#64748b', fontSize:'13px', fontWeight:'600', cursor:isPaymentSubmitting?'not-allowed':'pointer', marginBottom:'20px', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
          <FontAwesomeIcon icon={faArrowLeft}/> Back to Payers
        </button>

        {renderModeInfoPanel()}
        {renderEmployeeSelector()}

        <div style={{ background:T.cardBg, borderRadius:'20px', padding:'28px', border:`1.5px solid ${T.cardBorder}`, boxShadow:'0 4px 16px rgba(0,0,0,0.06)' }}>
          <div style={{ display:'flex', alignItems:'center', marginBottom:'24px', paddingBottom:'20px', borderBottom:`1.5px solid ${T.cardBorder}` }}>
            <div style={{ width:'56px', height:'56px', borderRadius:'16px', background:T.primaryGrad, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', marginRight:'16px', fontSize:'24px', fontWeight:'800', flexShrink:0, boxShadow:'0 4px 16px rgba(59,130,246,0.2)' }}>
              <FontAwesomeIcon icon={faUser} style={{ fontSize: '24px' }} />
            </div>
            <div>
              <h3 style={{ fontSize:'20px', fontWeight:'800', color:'#0f172a', margin:'0 0 4px', fontFamily:"'DM Sans',system-ui,sans-serif", letterSpacing:'-0.3px' }}>{selectedPayer.phoneLabel || 'Phone Number'}</h3>
              <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'5px' }}>
                <FontAwesomeIcon icon={faPhone} style={{ fontSize:'10px', color:'#94a3b8' }}/>
                <span style={{ fontSize:'12px', color:'#64748b' }}>+{selectedPayer.phone_country}{selectedPayer.phone_number}</span>
              </div>
              <span style={{ fontSize:'10px', fontWeight:'800', padding:'3px 12px', borderRadius:'20px', display:'inline-block', background:selectedPayer.network?.toLowerCase()==='mtn'?'#fef3c7':'#fee2e2', color:selectedPayer.network?.toLowerCase()==='mtn'?'#92400e':'#991b1b', letterSpacing:'0.5px' }}>
                {selectedPayer.network?.toUpperCase()||'N/A'}
              </span>
            </div>
          </div>

          <form onSubmit={handleChargePayment}>
            <div style={{ marginBottom:'24px' }}>
              <label style={labelStyle}>Amount ({clinicDetails.currency}) *</label>
              {activeMode!=='wallet'&&computedAmount!==null?(
                <div style={{ padding:'16px 20px', borderRadius:'14px', background:info.bgColor, border:`1.5px solid ${info.borderColor}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div>
                    <span style={{ fontSize:'12px', color:info.color, fontWeight:'700' }}>Auto-calculated</span>
                    <p style={{ margin:'1px 0 0', fontSize:'11px', color:'#94a3b8' }}>{PAYMENT_MODES.find(m=>m.id===activeMode)?.shortLabel}</p>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <span style={{ fontSize:'32px', fontWeight:'800', color:info.color, display:'block', lineHeight:1 }}>{computedAmount.toLocaleString()}</span>
                    <span style={{ fontSize:'11px', color:'#94a3b8', fontWeight:'500' }}>{clinicDetails.currency}</span>
                  </div>
                </div>
              ):(
                <>
                  <input type="text" name="amount" value={paymentForm.amount} onChange={handlePaymentInputChange} style={{...inputStyle(errors.amount),fontSize:'22px',fontWeight:'800'}} placeholder="0" disabled={isPaymentSubmitting}
                    onFocus={e=>e.target.style.borderColor=T.primary} onBlur={e=>e.target.style.borderColor=errors.amount?'#ef4444':'#e2e8f0'}/>
                  {clinicDetails.modeAmount&&clinicDetails.modeAmount!=='0'&&(<span style={{ fontSize:'11px', color:'#f59e0b', marginTop:'3px', display:'block' }}>💡 Suggested: {parseInt(clinicDetails.modeAmount).toLocaleString()} {clinicDetails.currency}</span>)}
                  {errors.amount&&<span style={errStyle}>{errors.amount}</span>}
                </>
              )}
            </div>
            <button type="submit" disabled={isPaymentSubmitting} style={{ width:'100%', padding:'14px', background:isPaymentSubmitting?'#cbd5e1':T.primaryGrad, color:'#fff', border:'none', borderRadius:'12px', fontSize:'15px', fontWeight:'800', cursor:isPaymentSubmitting?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'9px', fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:isPaymentSubmitting?'none':'0 4px 16px rgba(59,130,246,0.3)', transition:'all 0.2s ease' }}>
              {isPaymentSubmitting?<><FontAwesomeIcon icon={faSpinner} spin/> Processing…</>:'💰 Make Payment'}
            </button>
          </form>
        </div>

        <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:'14px', padding:'16px 20px', marginTop:'16px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'9px' }}><span>💡</span><h4 style={{ fontSize:'13px', fontWeight:'700', color:'#0369a1', margin:0 }}>Payment Instructions</h4></div>
          <div style={{ fontSize:'12px', color:'#0284c7', lineHeight:'1.7' }}>
            <p style={{ margin:'0 0 3px' }}>• Payment will be charged to your registered mobile money number</p>
            <p style={{ margin:'0 0 3px' }}>• A prompt will be sent to your phone — check and approve it</p>
            <p style={{ margin:0 }}>• The process takes about 30–60 seconds to confirm</p>
          </div>
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // PAYMENT SUCCESS (unchanged)
  // ════════════════════════════════════════════════════════════════════════════
  const renderPaymentSuccess = () => {
    const status=confirmationData?.charge_details?.status||confirmationData?.status;
    const isSucceeded=status==='succeeded';
    const isFailed=status==='failed';
    const isPending=!isSucceeded&&!isFailed;
    return (
      <div style={{ maxWidth:'540px', margin:'0 auto', padding:'20px 0' }}>
        {isPolling&&isPending&&(
          <div style={{ textAlign:'center' }}>
            <div style={{ width:'100px', height:'100px', borderRadius:'50%', background:'linear-gradient(135deg,#fef3c7,#fde68a)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 24px', animation:'pulse 2s infinite' }}>
              <FontAwesomeIcon icon={faClock} style={{ fontSize:'46px', color:'#f59e0b' }}/>
            </div>
            <h2 style={{ fontSize:'24px', fontWeight:'800', color:'#0f172a', marginBottom:'10px', fontFamily:"'DM Sans',system-ui,sans-serif", letterSpacing:'-0.4px' }}>Confirming Payment…</h2>
            <p style={{ color:'#64748b', fontSize:'14px', marginBottom:'22px' }}>Please wait while we verify your transaction</p>
            <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:'12px', padding:'18px', marginBottom:'22px', textAlign:'left' }}>
              <p style={{ color:'#92400e', fontSize:'13px', fontWeight:'700', margin:'0 0 5px' }}>🔄 Checking payment status</p>
              <p style={{ color:'#b45309', fontSize:'12px', margin:0 }}>Attempt {pollingAttempts+1} of 12 · polling every 10s</p>
            </div>
            <div style={{ width:'100%', height:'6px', background:'#e2e8f0', borderRadius:'3px', overflow:'hidden' }}>
              <div style={{ width:`${(pollingAttempts/12)*100}%`, height:'100%', background:'linear-gradient(90deg,#f59e0b,#f97316)', transition:'width 1s ease', borderRadius:'3px' }}/>
            </div>
          </div>
        )}

        {(showFinalStatus||isSucceeded||isFailed)&&(
          <div style={{ textAlign:'center' }}>
            <div style={{ width:'100px', height:'100px', borderRadius:'50%', background:isSucceeded?'linear-gradient(135deg,#d1fae5,#a7f3d0)':'linear-gradient(135deg,#fee2e2,#fecaca)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 24px', boxShadow:isSucceeded?'0 8px 32px rgba(16,185,129,0.25)':'0 8px 32px rgba(239,68,68,0.2)' }}>
              <FontAwesomeIcon icon={isSucceeded?faCheckCircle:faTimesCircle} style={{ fontSize:'52px', color:isSucceeded?'#10b981':'#ef4444' }}/>
            </div>
            <h2 style={{ fontSize:'28px', fontWeight:'800', color:isSucceeded?'#10b981':'#ef4444', marginBottom:'8px', fontFamily:"'DM Sans',system-ui,sans-serif", letterSpacing:'-0.5px' }}>
              {isSucceeded?'Payment Successful! 🎉':'Payment Failed'}
            </h2>
            <p style={{ color:'#64748b', fontSize:'14px', marginBottom:'24px' }}>
              {isSucceeded?'Your payment has been processed successfully.':'Something went wrong. Please try again or contact support.'}
            </p>
            {showFinalStatus&&(
              <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'12px', padding:'16px', marginBottom:'24px' }}>
                <div style={{ fontSize:'14px', fontWeight:'700', color:T.primary, marginBottom:'10px' }}>⏰ Auto-refresh in {statusTimer}s</div>
                <div style={{ width:'100%', height:'6px', background:'#e2e8f0', borderRadius:'3px', overflow:'hidden' }}>
                  <div style={{ width:`${((40-statusTimer)/40)*100}%`, height:'100%', background:statusTimer<=10?'#ef4444':statusTimer<=20?'#f59e0b':'#10b981', transition:'width 1s linear', borderRadius:'3px' }}/>
                </div>
              </div>
            )}
            {isSucceeded&&confirmationData?.clinic_balance_update&&(
              <div style={{ background:'linear-gradient(135deg,#f0fdf4,#dcfce7)', border:'1px solid #bbf7d0', borderRadius:'16px', padding:'22px', marginBottom:'24px', textAlign:'left' }}>
                <h3 style={{ fontSize:'14px', fontWeight:'800', color:'#166534', marginBottom:'16px', fontFamily:"'DM Sans',system-ui,sans-serif" }}>💰 Account Updated</h3>
                {[['Previous Balance',confirmationData.clinic_balance_update.previous_balance],['Amount Added',`+${parseInt(confirmationData.clinic_balance_update.amount_added).toLocaleString()}`]].map(([label,val],i)=>(
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px', paddingBottom:'10px', borderBottom:'1px solid #bbf7d0' }}>
                    <span style={{ color:'#166534', fontSize:'13px', fontWeight:'500' }}>{label}</span>
                    <span style={{ color:'#10b981', fontWeight:'700', fontSize:'13px' }}>{typeof val==='number'?parseInt(val).toLocaleString():val} {clinicDetails.currency}</span>
                  </div>
                ))}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ color:'#166534', fontSize:'15px', fontWeight:'800' }}>New Balance</span>
                  <span style={{ color:'#10b981', fontWeight:'800', fontSize:'22px' }}>{parseInt(confirmationData.clinic_balance_update.new_balance).toLocaleString()} {clinicDetails.currency}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {!isPolling&&!showFinalStatus&&!isSucceeded&&!isFailed&&(
          <div style={{ textAlign:'center' }}>
            <div style={{ width:'100px', height:'100px', borderRadius:'50%', background:'linear-gradient(135deg,#dbeafe,#bfdbfe)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 24px' }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize:'52px', color:T.primary }}/>
            </div>
            <h2 style={{ fontSize:'24px', fontWeight:'800', color:'#0f172a', marginBottom:'8px', fontFamily:"'DM Sans',system-ui,sans-serif" }}>Payment Initiated!</h2>
            <p style={{ color:'#64748b', fontSize:'14px', marginBottom:'24px' }}>A payment prompt has been sent to your phone.</p>
          </div>
        )}

        {!showFinalStatus&&(
          <div style={{ display:'flex', justifyContent:'center', marginTop:'20px' }}>
            <button onClick={handleBackToSelection} disabled={isPolling} style={{ padding:'12px 32px', background:isSucceeded?T.successGrad:T.primaryGrad, color:'#fff', border:'none', borderRadius:'12px', fontSize:'14px', fontWeight:'700', cursor:isPolling?'not-allowed':'pointer', opacity:isPolling?0.7:1, boxShadow:isPolling?'none':'0 4px 16px rgba(59,130,246,0.3)' }}>
              {isPolling?'Please Wait…':isSucceeded?'Make Another Payment':'Try Again'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ display:'flex', minHeight:'100vh', background:T.mainBg }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap" rel="stylesheet"/>
      {renderSidebar()}
      {renderTopBar()}
      <div style={{ marginLeft:`${SIDEBAR_WIDTH}px`, paddingTop:`${TOPBAR_HEIGHT+36}px`, padding:`${TOPBAR_HEIGHT+36}px 44px 44px`, minHeight:'100vh', width:`calc(100% - ${SIDEBAR_WIDTH}px)`, boxSizing:'border-box', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
        {errors.general&&(
          <div style={{ background:'#fee2e2', border:'1px solid #fecaca', borderRadius:'12px', padding:'14px 18px', marginBottom:'24px', display:'flex', alignItems:'center', gap:'10px' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color:'#dc2626', fontSize:'16px', flexShrink:0 }}/>
            <p style={{ color:'#991b1b', fontSize:'13px', margin:0, fontWeight:'600' }}>{errors.general}</p>
          </div>
        )}
        {view==='selectPayer'    && renderPayerSelection()}
        {view==='paymentSuccess' && renderPaymentSuccess()}
      </div>
      
      {/* Custom Prompt */}
      {prompt.show && (
        <CustomPrompt 
          message={prompt.message} 
          type={prompt.type} 
          onClose={() => setPrompt({ show: false, message: '', type: 'info' })}
        />
      )}
      
      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onConfirm={handleDeletePayer}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setPendingDeletePayer(null);
        }}
        title="⚠️ Dangerous Action"
        message={`You are about to permanently delete ${pendingDeletePayer?.phoneLabel || 'this payer'} (${pendingDeletePayer?.phone_number}).\n\nTHIS ACTION CANNOT BE REVERSED.\n\nAll payment history and records associated with this payer will be lost.\n\nAre you absolutely sure you want to proceed?`}
        confirmText="Yes, Delete Permanently"
        cancelText="Cancel"
        danger={true}
        isLoading={isDeletingPayer}
      />
      
      {/* Global Loading Overlay for initial load */}
      {loading && view === 'selectPayer' && !fetchingPayers && (
        <LoadingOverlay message="Loading clinic information..." />
      )}
      
      <style>{`
        @keyframes pulse{0%,100%{transform:scale(1);opacity:1;}50%{transform:scale(1.05);opacity:0.9;}}
        @keyframes slideIn{from{transform:translateX(100%);opacity:0;}to{transform:translateX(0);opacity:1;}}
        @keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
        @keyframes scaleIn{from{transform:scale(0.95);opacity:0;}to{transform:scale(1);opacity:1;}}
        *{box-sizing:border-box;}
        body{margin:0;padding:0;}
        input:focus,select:focus{outline:none;}
        button:active:not(:disabled){transform:scale(0.97)!important;}
        ::-webkit-scrollbar{width:6px;height:6px;}
        ::-webkit-scrollbar-track{background:#f1f5f9;border-radius:3px;}
        ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:3px;}
        ::-webkit-scrollbar-thumb:hover{background:#94a3b8;}
      `}</style>
    </div>
  );
}

export default PaymentNew;